import React, { memo, useMemo, useRef } from 'react'

import { cn } from '@/utils'

import { assert } from '@/utils/assert'
import { arrayIncludes } from '@/utils/primitives'

import { CueType } from '~shared/rundown/cue'
import { RundownPermission } from '~shared/rundown/permission'

import { useRundownAccess } from '@/features/permissions/lib/use-rundown-access.js'
import { useRundown } from '@/features/rundown/rundown.derived'

import { useIsNextHardStartCue } from './cue-timing.store'
import { useCueActions } from './cue.actions'
import { useCueId } from './cue.context'
import { useCuesDerived } from './cue.derived'
import { CueDropzone } from './cue.dnd'
import {
  useCue,
  useIndexedCue,
  useIsActiveCue,
  useIsCueInSoftSchedulePath,
  useIsRunnerNextCue,
} from './cue.hooks'
import { CueActiveMarker } from './CueActiveMarker'
import { CueCellList } from './CueCellList'
import { CueControls } from './CueControls'
import { CueDropIndicator } from './CueDropIndicator'
import { CueHeader } from './CueHeader'
import { CueIndex } from './CueIndex'
import { CueRowDropzone } from './CueRowDropzone'
import { CueStateIndicator } from './CueStateIndicator'
import { CueTitle } from './CueTitle'
import { Group } from './group/Group'
import { GroupTreeLines } from './group/GroupTreeLines'
import { Timing } from './timing/Timing'
import { TimingIndicator } from './timing/TimingIndicator'

export const CueItem = memo(function CueItem() {
  const { hasPermission } = useRundownAccess()
  const canEdit = hasPermission(RundownPermission.EDIT)
  const readonly = !canEdit

  const cueId = useCueId()

  const { firstCueId } = useCuesDerived()

  const cue = useCue(cueId)
  const meta = useIndexedCue(cueId)

  // Every rendered cue comes from the indexed cue list, which only contains
  // cues whose document has arrived, so both cue and meta are present.
  // Asserted once here rather than guarded at each read further down.
  assert(cue, `CueItem: no cue document for ${cueId}`)
  assert(meta, `CueItem: cue ${cueId} is missing from the indexed cue list`)

  const isActive = useIsActiveCue(cue.id)
  const isNext = useIsRunnerNextCue(cue.id)
  const isNextHardStart = useIsNextHardStartCue(cue.id)

  const rundown = useRundown()
  const rundownId = rundown.id
  const timezone = rundown.timezone
  const currentCueHighlightColor = rundown.settings.currentCueHighlightColor
  const isCueInSoftSchedulePath = useIsCueInSoftSchedulePath(cueId)

  // Parent group, for the styling a child inherits. Undefined for a root row.
  const parentCue = useCue(meta.parentId)

  const {
    handleSetNextCue,
    handleJumpToCue,
    handleUpdateCue,
    handleAddCue,
    handleDuplicateCue,
    handleDeleteCue,
    runFixAction,
    isPending,
  } = useCueActions(cueId)

  const parentBackgroundColour = meta.parentId
    ? parentCue?.backgroundColor
    : cue.backgroundColor
  const skipDuringShow =
    cue.settings.skipDuringShow ||
    (!!meta.parentId && (parentCue?.settings?.skipDuringShow ?? false))

  // Ref for heading title to enable keyboard navigation focus
  const headingTitleRef = useRef<HTMLDivElement | null>(null)

  const isFirstCue = cue.id === firstCueId

  const isTypeCue = useMemo(() => cue.type === CueType.CUE, [cue.type])

  const cueCellBackground = useMemo(() => {
    const userBgColor =
      cue.backgroundColor === 'transparent' ? undefined : cue.backgroundColor
    // When the user sets a currentCueHighlightColor in the rundown settings, apply that colour to an active cue
    const activeColor = isActive
      ? currentCueHighlightColor || userBgColor
      : undefined

    // Add drop indicator styling
    const backgroundColor = isTypeCue
      ? activeColor || userBgColor || '#161616'
      : userBgColor || 'black'

    return backgroundColor
  }, [cue.backgroundColor, currentCueHighlightColor, isActive, isTypeCue])

  const cellStyle = useMemo<React.CSSProperties>(() => {
    return {
      transition: 'ease 0.3s background-color',
      backgroundColor: cueCellBackground,
      borderRadius: '0.25rem',
    }
  }, [cueCellBackground])

  const cueStyle = useMemo<React.CSSProperties>(
    () => ({
      ['--cue-cell-bg-color' as string]: cueCellBackground, // Set background color as CSS variable for use in children
    }),
    [cueCellBackground],
  )

  const indexBgColor = useMemo(() => {
    if (isActive) return '#ef4444'
    if (skipDuringShow) return '#000000'
    if (cue.backgroundColor === 'transparent' && cue.type === CueType.CUE)
      return '#161616'
    return (cue.backgroundColor as string) || '#161616'
  }, [isActive, skipDuringShow, cue.backgroundColor, cue.type])

  return (
    <section
      role="row"
      aria-label="Cue row"
      id={cue.id}
      className={cn(
        'relative isolate px-2 w-full',
        isFirstCue && 'pt-2',
        isNextHardStart && 'scroll-m-24',
      )}
      style={cueStyle}
    >
      <CueDropIndicator
        position={CueDropzone.BEFORE}
        cueId={cue.id}
        parentId={meta.parentId}
      />

      {/* Line connecting scheduled soft cues */}
      {isCueInSoftSchedulePath && (
        // Emulate start time block left margin: 2 + 7 + 1 + 12 + 1 units
        <div className="absolute left-23 w-28">
          <div className="absolute left-1/2 -ml-[0.5px] w-px h-12 bg-white" />
        </div>
      )}

      {/* Active cue line above cue */}
      {isActive && <CueActiveMarker isFirstCue={isFirstCue} />}

      {/* Timing Indicator (e.g. "Gap of 5 minutes") */}
      {!isFirstCue && cue ? (
        <div className="min-h-3 ml-[84px] my-0.5 w-fit">
          <TimingIndicator
            cueId={cue.id}
            timezone={timezone ?? undefined}
            onRunFixAction={runFixAction}
            readonly={readonly}
          />
        </div>
      ) : null}

      {/* Next cue line above cue */}
      {isNext && (
        <div className="relative">
          <span
            className={[
              'mt-1 mb-4 w-full h-[2px] block bg-gray-400 outline-4 outline-black',
            ].join(' ')}
          ></span>
          <span className="absolute -top-[6px] ml-8 text-xs font-bold uppercase text-gray-300 bg-black px-2">
            Next cue
          </span>
        </div>
      )}

      <GroupTreeLines
        cueId={cue.id}
        cueType={cue.type}
        parentId={meta.parentId}
        parentBackgroundColour={parentBackgroundColour}
        lastSibling={meta.lastSibling}
        childrenCues={meta.children}
      />

      {/* Cue Item */}
      <div
        role="none"
        className="relative group flex items-stretch min-h-[84px] gap-1 w-full"
      >
        {/**
         * Controls
         *
         * This fixes the conflicts of `draggable` with `contenteditable` and <input>
         * fields by only activating the draggable attribute if the mouse if over the
         * draghandle area.
         */}
        <div className="z-30 w-7 flex-none">
          <CueControls
            cue={cue}
            readonly={readonly}
            parentId={meta.parentId}
            onUpdateCue={handleUpdateCue}
            onAddCue={handleAddCue}
            onDuplicateCue={handleDuplicateCue}
            onDelete={handleDeleteCue}
            onJumpToCue={handleJumpToCue}
            onSetNext={handleSetNextCue}
          />
        </div>

        <div
          role="none"
          className="relative group rounded-sm flex items-stretch min-h-[84px] gap-1 w-full"
        >
          <CueRowDropzone
            dropzone={
              cue.type === CueType.GROUP ? CueDropzone.GROUP : CueDropzone.CUE
            }
            cueId={cue.id}
            parentId={meta.parentId}
            lastSibling={meta.lastSibling}
          />

          {meta.lastSibling && meta.parentId && (
            <CueDropIndicator
              position={CueDropzone.AFTER}
              cueId={cue.id}
              parentId={meta.parentId}
            />
          )}

          {/* Index */}
          {arrayIncludes([CueType.CUE, CueType.GROUP], cue.type) && (
            <div
              className="relative w-12 flex-none flex"
              style={{
                ...cellStyle,
                backgroundColor: indexBgColor,
              }}
            >
              <CueStateIndicator
                cueType={cue.type}
                cueSettings={cue?.settings}
                className="absolute top-2 left-1/2 -translate-x-1/2"
                skipDuringShow={skipDuringShow}
              />
              <CueIndex
                cueId={cue.id}
                className={
                  meta.parentId
                    ? 'text-sm font-semibold'
                    : 'text-base font-semibold'
                }
                index={meta.displayIndex}
                readonly={readonly}
                loading={isPending}
                onSetNext={handleSetNextCue}
                onJumpToCue={handleJumpToCue}
                skipDuringShow={skipDuringShow}
              />
            </div>
          )}
          {isTypeCue ? (
            <Timing
              rundownId={rundownId}
              cueId={cue.id}
              timezone={timezone || 'UTC'}
              cellStyle={cellStyle}
              skipDuringShow={skipDuringShow}
            />
          ) : null}
          {cue.type === CueType.GROUP && (
            <Group
              cue={cue}
              childrenCues={meta.children ?? []}
              onUpdateCue={(title) => handleUpdateCue({ title })}
              readonly={readonly || cue.settings?.preventEdits}
              style={cellStyle}
            />
          )}
          {/* Title / Heading */}
          <div
            className={cn(
              'flex-none rounded-sm transition-all',
              cue.type === CueType.CUE && 'w-[326px]',
              cue.type === CueType.HEADING && 'w-full max-w-[610px]',
            )}
            style={cellStyle}
          >
            {cue.type === CueType.CUE && (
              <CueHeader
                rundownId={rundownId}
                readonly={readonly}
              />
            )}
            {cue.type === CueType.HEADING && (
              <div className="flex items-center h-full w-full">
                <CueTitle
                  className={cn(
                    'font-semibold w-full',
                    isPending && 'bg-animated from-gray-800 to-gray-900',
                  )}
                  inputRef={headingTitleRef}
                  value={cue.title}
                  onChange={(newTitle) => handleUpdateCue({ title: newTitle })}
                  disabled={isPending}
                  readonly={readonly || cue.settings?.preventEdits}
                />
              </div>
            )}
          </div>
          {/* Cells */}
          {arrayIncludes([CueType.CUE], cue.type) ? <CueCellList /> : null}
        </div>
      </div>
      {/* End Time */}
    </section>
  )
})
